import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import MainLayout from '@/components/layout/MainLayout';
import SubscriptionCard from '@/components/settings/SubscriptionCard';
import { useAppStore } from '@/store';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { MCPService, SubscriptionTier } from '@/types';
import MCPServiceCard from '@/components/settings/MCPServiceCard';
import NewsManager from '@/components/settings/NewsManager';
import OpenAISettings from '@/components/settings/OpenAISettings';
import OllamaSettings from '@/components/settings/OllamaSettings';
import ThemeToggle from '@/components/settings/ThemeToggle';
import GeminiSettings from '@/components/settings/GeminiSettings';
import DeepSeekSettings from '@/components/settings/DeepSeekSettings';
import CohereSettings from '@/components/settings/CohereSettings';
import MaplePTSettings from '@/components/settings/MaplePTSettings';
import { useProfile } from '@/hooks/useProfile'; // <-- Import useProfile
import LobbySettings from '@/components/settings/LobbySettings';
import ModelSettings from '@/components/settings/ModelSettings';
import LogViewer from '@/components/settings/LogViewer';
import AnalyticsDashboard from '@/components/settings/AnalyticsDashboard';
import SubscriptionManager from '@/components/settings/SubscriptionManager';
import MarketingSettings from '@/components/settings/MarketingSettings';
import LandingPageSettings from '@/components/settings/LandingPageSettings';
import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client'; // Import supabase
import { useToast } from '@/hooks/use-toast'; // Import useToast
import SealedMemory from '@/components/settings/SealedMemory';
import UpdatesCard from '@/components/settings/UpdatesCard';

const Settings = () => {
  const { user, textSize, setTextSize, saveHistory, setSaveHistory, allowAnalytics, setAllowAnalytics } = useAppStore();
  const { chats, deleteAllChats, refreshChats } = useSupabaseStore();
  const { profile } = useProfile(); // <-- Get profile from Supabase
  const [isConfirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const { currentTier, loading: subscriptionLoading, createCustomerPortal, loading: portalLoading } = useSubscription();
  const navigate = useNavigate();
  const { toast } = useToast(); // Get toast function
  const [isBackfilling, setIsBackfilling] = useState(false); // State for backfill button
  // NEW: Trust Mode default state
  const [trustDefault, setTrustDefault] = useState<boolean>(() => {
    const saved = localStorage.getItem('trust_mode_enabled');
    return saved === 'true';
  });
  // NEW: trust options
  const [requireCitations, setRequireCitations] = useState<boolean>(() => {
    return localStorage.getItem('trust_require_citations') === 'true';
  });
  const [requireAttestation, setRequireAttestation] = useState<boolean>(() => {
    return localStorage.getItem('trust_attestation_enabled') === 'true';
  });

  const handleTrustDefaultChange = (checked: boolean) => {
    setTrustDefault(checked);
    localStorage.setItem('trust_mode_enabled', checked ? 'true' : 'false');
    toast({
      title: checked ? "Trust Mode default enabled" : "Trust Mode default disabled",
      description: checked
        ? "New sessions will start with Trust Mode on."
        : "New sessions will start with Trust Mode off.",
    });
  };

  // NEW: toggle handlers
  const handleRequireCitationsChange = (checked: boolean) => {
    setRequireCitations(checked);
    localStorage.setItem('trust_require_citations', checked ? 'true' : 'false');
    toast({
      title: checked ? "Citation enforcement enabled" : "Citation enforcement disabled",
      description: checked
        ? "Answers will include citations when external context is used."
        : "Answers will no longer require citations.",
    });
  };

  const handleRequireAttestationChange = (checked: boolean) => {
    setRequireAttestation(checked);
    localStorage.setItem('trust_attestation_enabled', checked ? 'true' : 'false');
    toast({
      title: checked ? "Attestation required" : "Attestation disabled",
      description: checked
        ? "You will be asked to confirm before sealing outputs."
        : "Sealing will proceed without confirmation.",
    });
  };

  const isAdmin = profile?.role === "admin"; // <-- Check admin role

  const handleSaveHistoryChange = (checked: boolean) => {
    if (!checked && chats.length > 0) {
      setConfirmDeleteDialogOpen(true);
    } else {
      setSaveHistory(checked);
      refreshChats();
    }
  };

  const handleManageSubscription = async () => {
    if (currentTier === 'free') {
      navigate('/purchase');
    } else {
      await createCustomerPortal();
    }
  };

  const handleConfirmDeleteAndDisableHistory = async () => {
    if (user?.id) {
      try {
        await deleteAllChats(user.id);
        setSaveHistory(false);
        refreshChats();
      } catch (error) {
        console.error("Failed to delete chats and disable history", error);
      }
    }
  };

  const handleBackfillStripeIds = async () => {
    setIsBackfilling(true);
    toast({
      title: "Starting Backfill",
      description: "Attempting to sync Stripe Customer IDs for existing subscribers...",
    });
    try {
      const { data, error } = await supabase.functions.invoke('backfill-stripe-customer-ids');
      if (error) {
        throw error;
      }
      toast({
        title: "Backfill Complete",
        description: data.message || "Process finished.",
      });
    } catch (error: any) {
      toast({
        title: "Backfill Failed",
        description: error.message || "An unknown error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">Settings</h1>
        
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="general">General</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="news">News</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="logs">Logs</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="marketing">Marketing</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="landing-page">Landing Page</TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="general">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ThemeToggle />
              <UpdatesCard />

              <Card>
                <CardHeader>
                  <CardTitle>Subscription</CardTitle>
                  <CardDescription>Manage your billing and subscription plan.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-sm">
                    Your current plan is: <span className="font-semibold">{subscriptionLoading ? 'Loading...' : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}</span>
                  </p>
                  <Button 
                    className="w-full" 
                    onClick={handleManageSubscription} 
                    disabled={subscriptionLoading || portalLoading}
                  >
                    {portalLoading ? 'Redirecting...' : (currentTier === 'free' ? 'Upgrade Plan' : 'Manage Subscription')}
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                  <CardDescription>
                    Control when and how you receive notifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="push-notifications">Push Notifications</Label>
                      <Switch id="push-notifications" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-notifications">Email Notifications</Label>
                      <Switch id="email-notifications" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Privacy</CardTitle>
                  <CardDescription>
                    Control your privacy settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="save-history">Save Chat History</Label>
                      <Switch 
                        id="save-history" 
                        checked={saveHistory}
                        onCheckedChange={handleSaveHistoryChange}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="analytics">Allow Usage Analytics</Label>
                      <Switch
                        id="analytics"
                        checked={allowAnalytics}
                        onCheckedChange={setAllowAnalytics}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Accessibility</CardTitle>
                  <CardDescription>
                    Customize accessibility settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="block mb-3">Text Size</Label>
                      <RadioGroup
                        value={textSize}
                        onValueChange={(value) => setTextSize(value as 'small' | 'medium' | 'large')}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="small" id="small" />
                          <Label htmlFor="small">Small</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="medium" id="medium" />
                          <Label htmlFor="medium">Medium</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="large" id="large" />
                          <Label htmlFor="large">Large</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* NEW: Sealed Memory content */}
          <TabsContent value="sealed-memory">
            <SealedMemory />
          </TabsContent>
          
          <TabsContent value="api-keys">
            <div className="grid grid-cols-1 gap-6">
              <h2 className="text-2xl font-bold">API Integrations</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <OpenAISettings />
                <OllamaSettings />
                <GeminiSettings />
                <DeepSeekSettings />
                <CohereSettings />
                <MaplePTSettings />
              </div>
            </div>
          </TabsContent>
          
          {isAdmin && (
            <TabsContent value="models">
              <ModelSettings />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="news">
              <NewsManager />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="lobby">
              <LobbySettings />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="logs">
              <LogViewer />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="analytics">
              <AnalyticsDashboard />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="subscriptions">
              <SubscriptionManager />
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Data Sync</CardTitle>
                  <CardDescription>One-time actions to sync data between services.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="backfill-stripe" className="flex flex-col space-y-1">
                      <span>Backfill Stripe Customer IDs</span>
                      <span className="font-normal leading-snug text-muted-foreground">
                        Sync Stripe Customer IDs to user profiles for existing subscribers.
                      </span>
                    </Label>
                    <Button
                      id="backfill-stripe"
                      onClick={handleBackfillStripeIds}
                      disabled={isBackfilling}
                    >
                      {isBackfilling ? 'Syncing...' : 'Run Sync'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="marketing">
              <MarketingSettings />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="landing-page">
              <LandingPageSettings />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <AlertDialog open={isConfirmDeleteDialogOpen} onOpenChange={setConfirmDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all of your chat history from our servers. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeleteAndDisableHistory}>
              Delete and Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
};

export default Settings;